"use client";

import { IconCheckCircleFill } from "@decibeltrade/react-icons";
import { useTranslations } from "next-intl";
import type { ComponentProps } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { css } from "styled-system/css";
import { flex, stack } from "styled-system/patterns";

import { CARD_VAULT_ANNOUNCEMENT_ID } from "@/app/(main)/card-vault/constants";
import { Tag } from "@/components/tag";
import { Announcement, AnnouncementCategory } from "@/contexts/queries/announcements";
import { formatDate } from "@/utils/format";

interface AnnouncementCardProps {
  announcement: Announcement;
  isFirst: boolean;
}

const categoryLabels: Record<AnnouncementCategory, string> = {
  listings: "newMarkets",
  delistings: "delistedMarkets",
  "parameter-updates": "parameterUpdates",
  "product-updates": "productUpdates",
  community: "community",
};

export function AnnouncementCard({ announcement, isFirst }: AnnouncementCardProps) {
  const t = useTranslations("announcements");
  // Card Vault is the only long-lived announcement worth translating today;
  // other entries (market listings, parameter updates) are short-lived and
  // ship en-only by design.
  const isCardVault = announcement.id === CARD_VAULT_ANNOUNCEMENT_ID;
  const title = isCardVault ? t("cardVaultEntryTitle") : announcement.title;
  const body = isCardVault ? t("cardVaultEntryBody") : announcement.body;

  return (
    <div
      className={css({
        display: "flex",
        gap: "16",
        alignItems: "stretch",
        px: "20",
        rounded: "350",
        transitionPreset: "colors",
        _hover: {
          bg: "background.primary",
        },
        ".group:not(:hover) &": {
          // Highlight the first announcement when none of the other announcements are hovered
          bg: isFirst ? "background.primary" : undefined,
        },
      })}
    >
      <TimelineIndicator isFirst={isFirst} />

      {/* Content */}
      {/* minW: 0 lets this column shrink below its content's intrinsic width, so a
          wide table scrolls inside its own wrapper instead of widening the page. */}
      <div className={stack({ gap: "8", py: "24", flex: "1", minW: "0" })}>
        {/* Header */}
        <div className={stack({ gap: "4", align: "flex-start" })}>
          <div
            className={flex({
              w: "full",
              gap: "4",
              justify: "space-between",
              align: { base: "flex-start", desktop: "center" },
              direction: { base: "column", desktop: "row" },
            })}
          >
            <span className={css({ textStyle: "heading.2xs", color: "text.primary" })}>
              {title}
            </span>

            {/* Tags */}
            <div className={flex({ gap: "4", alignItems: "center" })}>
              {isFirst && <Tag variant="positive">{t("latest")}</Tag>}
              <Tag variant="secondary">
                {t(`categories.${categoryLabels[announcement.category]}`)}
              </Tag>
            </div>
          </div>
          <span className={css({ textStyle: "body.md", color: "text.tertiary" })}>
            {formatDate(announcement.timestamp)}
          </span>
        </div>

        {/* Body */}
        <div className={markdownStyles}>
          <Markdown components={markdownComponents} remarkPlugins={[remarkGfm]}>
            {body}
          </Markdown>
        </div>
      </div>
    </div>
  );
}

const markdownComponents: ComponentProps<typeof Markdown>["components"] = {
  a: ({ children, href }: ComponentProps<"a">) => {
    const isInternal =
      href != null &&
      (href.startsWith("/") ||
        href.startsWith("#") ||
        (typeof window !== "undefined" && href.startsWith(window.location.origin)));

    return (
      <a href={href} {...(!isInternal && { target: "_blank", rel: "noopener noreferrer" })}>
        {children}
      </a>
    );
  },
  // Wide tables (e.g. fee schedules) would otherwise force the whole card to
  // scroll horizontally on narrow viewports.
  table: ({ children }: ComponentProps<"table">) => (
    <div className={tableScrollStyles}>
      <table>{children}</table>
    </div>
  ),
};

// --- Sub-components & styles -----------------------------------------------------

function TimelineIndicator({ isFirst }: { isFirst: boolean }) {
  return (
    <div className={timelineIndicatorStyles}>
      {isFirst ? (
        <IconCheckCircleFill className={timelineIconStyles} />
      ) : (
        <div className={timelineDotStyles} />
      )}
      <div className={timelineVerticalLineStyles} />
    </div>
  );
}

const timelineIndicatorStyles = css({
  position: "relative",
  flexShrink: 0,
  w: "20",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
});

const timelineIconStyles = css({
  w: "20",
  h: "20",
  color: "text.positive",
  flexShrink: 0,
  mt: "24",
  position: "relative",
  zIndex: "[1]",
});

const timelineDotStyles = css({
  w: "12",
  h: "12",
  m: "4",
  rounded: "full",
  bg: "background.tertiary",
  flexShrink: 0,
  mt: "28",
  position: "relative",
  zIndex: "[1]",
});

const timelineVerticalLineStyles = css({
  position: "absolute",
  left: "[50%]",
  transform: "translateX(-50%)",
  top: "[44px]",
  h: "[calc(100% - 20px)]",
  w: "1",
  bg: "background.tertiary",
});

const markdownStyles = css({
  maxW: "[80ch]",
  textStyle: "body.lg",
  color: "text.tertiary",
  "& p": {
    mb: "8",
    _last: { mb: "0" },
  },
  "& strong": {
    color: "text.secondary",
    fontWeight: "medium",
  },
  "& a": {
    // Link labels are usually bare URLs with no break opportunities, so they
    // would otherwise widen the card on narrow screens.
    overflowWrap: "anywhere",
    color: "text.link",
    textDecoration: "underline",
    textDecorationSkipInk: "none",
    textUnderlinePosition: "from-font",
    _hover: { color: "text.link.hover" },
  },
  "& hr": {
    my: "12",
    borderColor: "border.secondary",
  },
  "& ul, & ol": {
    pl: "20",
    mb: "8",
    _last: { mb: "0" },
  },
  "& ul": {
    listStyleType: "disc",
  },
  "& ol": {
    listStyleType: "decimal",
  },
  "& li": {
    mb: "4",
    _last: { mb: "0" },
  },
  "& table": {
    w: "full",
    borderCollapse: "collapse",
    textAlign: "left",
  },
  "& th, & td": {
    px: { base: "8", desktop: "12" },
    py: "8",
    verticalAlign: "top",
    _first: { pl: "0" },
    _last: { pr: "0" },
  },
  "& th": {
    color: "text.secondary",
    fontWeight: "medium",
    borderBottom: "1px solid",
    borderColor: "border.secondary",
  },
  "& td": {
    borderBottom: "1px solid",
    borderColor: "border.tertiary",
    // Keeps paired values such as "0.045% / 0.015%" on one line. Headers stay
    // wrappable so they don't dictate the table's width on narrow screens.
    whiteSpace: "nowrap",
  },
  "& tbody tr:last-child td": {
    borderBottom: "none",
  },
});

const tableScrollStyles = css({
  w: "full",
  mb: "8",
  overflowX: "auto",
  _last: { mb: "0" },
});
